import { useId } from "react";

/**
 * A labelled form control.
 *
 * HQ-A11Y-003. Static inspection found 866 inputs, selects and textareas with
 * no id, aria-label, aria-labelledby or enclosing label. The dominant pattern
 * across HQ is
 *
 *     <label className="label">Start date</label>
 *     <input type="date" className="input" … />
 *
 * which looks correct and associates nothing: the label is a sibling with no
 * htmlFor, so a screen reader announces "edit text, blank" and clicking the
 * label does not focus the field. Staging /register exposed all four of its
 * fields as unnamed text fields in the browser's accessibility tree.
 *
 * These components make the association automatic. useId() produces an id that
 * is stable across server and client renders and unique per instance, so two
 * copies of the same form on one page do not collide — which is why this is not
 * a matter of "just add an id" by hand.
 *
 * Help and error text are wired through aria-describedby, so they are announced
 * with the field rather than sitting nearby unread.
 */
interface FieldShell {
  label: string;
  /** Sits under the control; announced as part of the field. */
  help?: string;
  /** Replaces help when present, and marks the control invalid. */
  error?: string | null;
  required?: boolean;
  className?: string;
}

function useFieldIds(hasHelp: boolean, hasError: boolean) {
  const base = useId();
  return {
    id: `${base}-input`,
    helpId: hasHelp ? `${base}-help` : undefined,
    errorId: hasError ? `${base}-error` : undefined,
  };
}

function Shell({
  label, help, error, required, id, helpId, errorId, children,
}: FieldShell & { id: string; helpId?: string; errorId?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="label" htmlFor={id}>
        {label}{required && <span aria-hidden="true"> *</span>}
        {required && <span className="sr-only"> (required)</span>}
      </label>
      {children}
      {error
        ? <p id={errorId} className="mt-1 text-xs text-bad">{error}</p>
        : help && <p id={helpId} className="mt-1 text-xs text-gray2">{help}</p>}
    </div>
  );
}

type InputProps = FieldShell & Omit<React.InputHTMLAttributes<HTMLInputElement>, "id" | "className">;

export function Field({ label, help, error, required, className, ...rest }: InputProps) {
  const { id, helpId, errorId } = useFieldIds(!!help, !!error);
  return (
    <Shell label={label} help={help} error={error} required={required}
      id={id} helpId={helpId} errorId={errorId}>
      <input
        id={id}
        className={className ?? "input"}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : helpId}
        {...rest}
      />
    </Shell>
  );
}

type SelectProps = FieldShell & Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "id" | "className">;

export function SelectField({ label, help, error, required, className, children, ...rest }: SelectProps) {
  const { id, helpId, errorId } = useFieldIds(!!help, !!error);
  return (
    <Shell label={label} help={help} error={error} required={required}
      id={id} helpId={helpId} errorId={errorId}>
      <select
        id={id}
        className={className ?? "input"}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : helpId}
        {...rest}
      >{children}</select>
    </Shell>
  );
}

type TextareaProps = FieldShell & Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "id" | "className">;

export function TextareaField({ label, help, error, required, className, ...rest }: TextareaProps) {
  const { id, helpId, errorId } = useFieldIds(!!help, !!error);
  return (
    <Shell label={label} help={help} error={error} required={required}
      id={id} helpId={helpId} errorId={errorId}>
      <textarea
        id={id}
        className={className ?? "input"}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : helpId}
        {...rest}
      />
    </Shell>
  );
}

/**
 * A group of related choices — radios, or checkboxes that answer one question.
 *
 * A visible heading over a set of checkboxes is not a label for any of them.
 * fieldset/legend is how that relationship is expressed, and it is what lets
 * someone hear "Departments, group" before the options rather than five
 * unrelated checkboxes.
 */
export function FieldGroup({ legend, help, children }: {
  legend: string; help?: string; children: React.ReactNode;
}) {
  const base = useId();
  const helpId = help ? `${base}-help` : undefined;
  return (
    <fieldset aria-describedby={helpId}>
      <legend className="label">{legend}</legend>
      {help && <p id={helpId} className="mb-1 text-xs text-gray2">{help}</p>}
      {children}
    </fieldset>
  );
}
